import { SVGProps } from 'react';

// Editor chrome icons — same visual language as components/icons/index.tsx
// (24×24 viewBox, stroke = currentColor, round caps, strokeWidth 2).

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

function base(size: number, props: SVGProps<SVGSVGElement>) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
    ...props,
  };
}

// ── Tab icons ──

/** Preset: magic wand with sparkles */
export function PresetIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M15 4V2M15 10V8M11.5 6h2M18.5 6h2M19 15l-1.5-1.5M4 20L13 11" />
      <path d="M20 20l-2-2" />
    </svg>
  );
}

/** Body: standing figure */
export function BodyIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <circle cx="12" cy="4.5" r="2.5" />
      <path d="M12 9v6M12 9l-4 2M12 9l4 2M12 15l-2.5 6M12 15l2.5 6" />
    </svg>
  );
}

/** Face: smiling face */
export function FaceIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5s1.2 1.8 3.5 1.8 3.5-1.8 3.5-1.8" />
      <path d="M9 9.5h.01M15 9.5h.01" />
    </svg>
  );
}

/** Hair: head profile with hair swoosh */
export function HairIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M6 21c0-4 -1.5-5 -1.5-9A7.5 7.5 0 0 1 19 9.5c0 2 -.8 3 -.8 3" />
      <path d="M4.5 12c2.5-1 4-3.5 4.5-6M12 4.5c-.5 2.5-2 4.5-4 5.5" />
    </svg>
  );
}

/** Outfit: t-shirt */
export function OutfitIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M8 4l-5 4 2.5 3L8 9.5V20h8V9.5l2.5 1.5L21 8l-5-4a3.5 3.5 0 0 1-8 0z" />
    </svg>
  );
}

/** Accessories: glasses */
export function AccessoriesIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <circle cx="6.5" cy="14.5" r="3.5" />
      <circle cx="17.5" cy="14.5" r="3.5" />
      <path d="M10 14.5c.5-1 3.5-1 4 0M3 14l1.5-6M21 14l-1.5-6" />
    </svg>
  );
}

/** Gestures: waving hand */
export function GesturesIcon({ size = 18, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M7 11.5V6a1.5 1.5 0 0 1 3 0v4M10 10V4.5a1.5 1.5 0 0 1 3 0V10M13 10V6a1.5 1.5 0 0 1 3 0v6.5a5.5 5.5 0 0 1-11 0V9a1.5 1.5 0 0 1 2 -1.4" />
      <path d="M19.5 8.5c1 1 1.5 2.5 1.5 4" />
    </svg>
  );
}

// ── Body-tab group icons ──

/** Stature: vertical ruler arrows */
export function StatureIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M12 3v18M8.5 6.5L12 3l3.5 3.5M8.5 17.5L12 21l-3.5-3.5M15.5 17.5L12 21" />
    </svg>
  );
}

/** Build: dumbbell */
export function BuildIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M9 12h6" />
      <rect x="4" y="8.5" width="3" height="7" rx="1" />
      <rect x="17" y="8.5" width="3" height="7" rx="1" />
      <path d="M2 10.5v3M22 10.5v3" />
    </svg>
  );
}

/** Limbs: flexed arm */
export function LimbsIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M5 20c0-5 2-8 5-9.5M10 10.5C10 8 11 5 13 4l3 2c-1 1.5-1.5 3-1.5 4.5 3 .5 5 3 5 6.5v3H5z" />
    </svg>
  );
}

/** Frame: shoulders/torso frame */
export function FrameIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M4 20v-4a6 6 0 0 1 4-5.7M20 20v-4a6 6 0 0 0-4-5.7" />
      <circle cx="12" cy="6" r="3" />
      <path d="M8 20v-3M16 20v-3" />
    </svg>
  );
}

// ── Button icons ──

/** Randomize: die */
export function DiceIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M9 9h.01M15 9h.01M12 12h.01M9 15h.01M15 15h.01" strokeWidth="2.6" />
    </svg>
  );
}

/** Reset: rotate counter-clockwise */
export function ResetIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M3 8V3m0 5h5M3.5 8A9 9 0 1 1 3 13.5" />
    </svg>
  );
}

/** Delete: trash */
export function TrashIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M4 7h16M9.5 7V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v2M6.5 7l1 13h9l1-13M10 11v5M14 11v5" />
    </svg>
  );
}

/** Back: chevron left */
export function BackIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M14.5 6L8.5 12l6 6" />
    </svg>
  );
}

/** Save: check */
export function CheckIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M4.5 12.5L10 18 19.5 7" />
    </svg>
  );
}

/** Warning: triangle alert (save errors) */
export function AlertIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M12 3.5L22 20H2z" />
      <path d="M12 10v4.5M12 17.2h.01" />
    </svg>
  );
}

/** Pieces: layered squares (outfit pieces section) */
export function PiecesIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <path d="M12 3l9 5-9 5-9-5z" />
      <path d="M3 13l9 5 9-5" />
    </svg>
  );
}
